import {PhoneNumberTextField} from '@hconnect/uikit'
import {GenericInput} from '@hm/fundamental'
import React from 'react'
import {Controller, FieldValues, Path} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {UserInformationInputSkeleton} from '../Skeleton/UserInformationInputSkeleton'

import {UserInformationProps} from './types'
import {useStyles} from './UserInput.styles'

type UserMobileNumberInputProps<T extends FieldValues> = UserInformationProps<T> & {
  canEditUserEmailPhone: boolean
}

export const UserMobileNumberInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  canEditUserEmailPhone,
  isLoading,
  isQuickPreview,
  name
}: UserMobileNumberInputProps<T>) => {
  const {t} = useTranslation()
  const errors = formMethods.formState.errors
  const classes = useStyles()
  const values = formMethods.watch()
  const country = formMethods.getValues()?.country
  if (isLoading) {
    return <UserInformationInputSkeleton />
  }
  const hasError = !!errors.mobileNumber?.message
  const localKey = `errorMessages.${errors.mobileNumber?.message}`
  const helperText = hasError ? t(localKey) : undefined

  return (
    <>
      {!readonlyMode || isQuickPreview ? (
        <Controller
          name={name}
          control={formMethods.control}
          rules={{
            validate: (value: string) =>
              formMethods.formState.isSubmitting &&
              value.trim().length === 0 &&
              formMethods.watch('eMail' as Path<T>).length === 0
                ? 'PHONE_OR_EMAIL_REQUIRED'
                : true
          }}
          render={({field}) => (
            <PhoneNumberTextField
              inputRef={field.ref}
              {...field}
              data-test-id={`userInformationForm-${field.name}`}
              className={isQuickPreview ? classes.quickPreviewTextField : classes.textField}
              fullWidth
              id="mobileNumber"
              label={t('userInformationForm.mobileNumber')}
              onPhoneChange={(phoneNumber) => {
                field.onChange(phoneNumber)
              }}
              defaultValue={values.mobileNumber}
              margin="normal"
              error={hasError}
              errorMsg={helperText}
              helperText={helperText}
              style={{maxWidth: isQuickPreview ? undefined : 400}}
              disabled={!canEditUserEmailPhone}
              InputLabelProps={{shrink: isQuickPreview ? true : undefined}}
              InputProps={{
                readOnly: readonlyMode,
                disableUnderline: readonlyMode || isQuickPreview,
                style: readonlyMode
                  ? {width: isQuickPreview ? undefined : 260}
                  : {maxWidth: isQuickPreview ? undefined : 400}
              }}
              options={{
                nationalMode: !readonlyMode,
                allowDropdown: !readonlyMode,
                initialCountry: values.mobileNumber ? undefined : country
              }}
              readonlyMode={readonlyMode}
              showNewUserProfile={true}
            />
          )}
        />
      ) : (
        <GenericInput
          data-cy="userInformationForm-mobileNumber"
          label={t('userInformationForm.mobileNumber')}
          value={values.mobileNumber || '-'}
          isReadOnly={true}
        />
      )}
    </>
  )
}
